import { useEffect, useRef, useState } from 'react';
import { Wws_Page } from '../../types/graphql';
import { getSectionId } from '../../components/Templates/SinglePageTemplate';
import useTracking from '../useTracking';
import { useCurrentRoute } from '../useCurrentRoute';

export const handleOnIntersection = (entries: IntersectionObserverEntry[]) => {
  let intersectedSection: string | undefined = undefined;

  entries.forEach((entry: IntersectionObserverEntry) => {
    if (!entry.isIntersecting) {
      return;
    }
    intersectedSection = entry.target.id
  });

  return intersectedSection;
}

export const useOnViewingSectionTracking = () => {
  const currentRoute = useCurrentRoute();
  const userDecisionArea = 'single page section';
  const trackGuest = useTracking('guest', currentRoute, userDecisionArea);
  const timeoutRef = useRef<NodeJS.Timeout>(undefined);
  const [activeSectionId, setActiveSectionId] = useState<string | undefined>();

  useEffect(() => {
    if (!activeSectionId) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      trackGuest(activeSectionId);
    }, 2000);

    return () => {
      clearTimeout(timeoutRef.current)
    };
  }, [activeSectionId, trackGuest]);


  return { setTrackingSection: setActiveSectionId };
}

const useGetActiveSinglePageSection = (
  pages: Wws_Page[],
  isSinglePageLayout: boolean = false
): string | undefined => {
  const [activeSectionId, setActiveSectionId] = useState<string | undefined>();
  const { setTrackingSection } = useOnViewingSectionTracking();

  useEffect(() => {
    if (!activeSectionId) {
      return;
    }

    setTrackingSection(activeSectionId);
  }, [activeSectionId, setTrackingSection]);


  const onIntersection = (entries: IntersectionObserverEntry[]) => {
    const intersectedSection = handleOnIntersection(entries);

    if (intersectedSection) {
      setActiveSectionId(intersectedSection);
    }
  };

  const onObserverSetUp = (
    pages: Wws_Page[],
    observer: IntersectionObserver
  ) => {
    const pageIds = pages.map((page) => getSectionId(page));
    pageIds.forEach((pageId) => {
      const element = document.getElementById(pageId);
      if (element) {
        observer.observe(element);
      }
    });
  };

  useEffect(() => {
    if (!isSinglePageLayout || !pages.length) {
      return;
    }

    const observer = new IntersectionObserver(
      onIntersection,
      {
        rootMargin: '-40% 0px -60% 0px',
        root: document,
        // threshold: [0.0001, 0.001, 0.01, 0.1, 0.25, 0.5, 1]
      }
    );

    onObserverSetUp(pages, observer);

    return () => {
      observer.disconnect();
    };
  }, [pages, isSinglePageLayout]);

  return activeSectionId;
};

export default useGetActiveSinglePageSection;
